import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { LiveCall } from "@/lib/liveCall";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Copy,
  PhoneOff,
  Video,
  ListChecks,
  Loader2,
  Users,
  Wifi,
  Circle,
  CircleOff,
  Pencil,
  Sparkles,
} from "lucide-react";

export default function LiveHost() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const callRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const localStreamRef = useRef(null);
  const recorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const audioCtxRef = useRef(null);
  const compositorRef = useRef(null); // { canvas, ctx, raf, remoteVideo, localVideo }
  const chunkSeqRef = useRef(0); // monotonic counter for streaming chunk uploads
  const chunkUploadFailuresRef = useRef(0); // back off after repeated failures
  const recordingStartRef = useRef(0);

  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissionError, setPermissionError] = useState(null);
  const [hasRemote, setHasRemote] = useState(false);
  const [connState, setConnState] = useState("new");
  const [connTimedOut, setConnTimedOut] = useState(false);
  const connTimerRef = useRef(null);
  // Interviewer notes
  const [notes, setNotes] = useState("");
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesSavedAt, setNotesSavedAt] = useState(null);
  const notesDebounceRef = useRef(null);
  const notesInitialLoadRef = useRef(false);
  const [summarizing, setSummarizing] = useState(false);
  const [notesSummary, setNotesSummary] = useState(null);
  // AI coach (live tailored questions)
  const [coachQuestions, setCoachQuestions] = useState([]);
  const [coachLoading, setCoachLoading] = useState(false);
  const [askedQuestions, setAskedQuestions] = useState({});
  const coachAutoFiredRef = useRef(false);
  const [muted, setMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordEnabled, setRecordEnabled] = useState(true);
  const [uploadingRecording, setUploadingRecording] = useState(false);

  const candidateLink = room
    ? `${window.location.origin}/live/${room.id}`
    : "";

  useEffect(() => {
    api
      .get(`/live-rooms/${roomId}`)
      .then(async ({ data }) => {
        // Auto-revive: if this room was ended (e.g. previous test), spin up
        // a fresh one so the recruiter doesn't share a dead candidate link.
        if (data.status === "ended" && data.job_id) {
          try {
            const { data: fresh } = await api.post(
              `/jobs/${data.job_id}/live-rooms`
            );
            navigate(`/jobs/${data.job_id}/live/${fresh.id}`, { replace: true });
            return;
          } catch {
            // fall back to showing the ended state — at least better than blank
          }
        }
        setRoom(data);
      })
      .catch(() => {
        toast.error("Live room not found");
        navigate(-1);
      })
      .finally(() => setLoading(false));
  }, [roomId, navigate]);

  // Poll room for candidate progress (name + step) until they're in live.
  useEffect(() => {
    if (!room || hasRemote) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/live-rooms/${roomId}`);
        setRoom(data);
      } catch {}
    }, 2500);
    return () => clearInterval(t);
  }, [roomId, room, hasRemote]); // eslint-disable-line

  // Load interviewer notes once on mount
  useEffect(() => {
    if (!room || notesInitialLoadRef.current) return;
    notesInitialLoadRef.current = true;
    api
      .get(`/live-rooms/${roomId}/notes`)
      .then(({ data }) => setNotes(data.notes || ""))
      .catch(() => {
        /* fresh room, no notes yet */
      });
  }, [room, roomId]);

  // Debounced auto-save of interviewer notes (1.5s after typing stops)
  useEffect(() => {
    if (!notesInitialLoadRef.current) return; // skip initial load population
    if (notesDebounceRef.current) clearTimeout(notesDebounceRef.current);
    notesDebounceRef.current = setTimeout(async () => {
      setNotesSaving(true);
      try {
        await api.post(`/live-rooms/${roomId}/notes`, { notes });
        setNotesSavedAt(Date.now());
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not save notes");
      } finally {
        setNotesSaving(false);
      }
    }, 1500);
    return () => clearTimeout(notesDebounceRef.current);
  }, [notes, roomId]);

  // Auto-generate AI coach questions once the candidate has reached the live
  // step (we have name + linked résumé context by then). Server-side cache
  // means re-fires are free.
  useEffect(() => {
    if (coachAutoFiredRef.current) return;
    if (!room) return;
    const ready = room.candidate_step === "live" || room.candidate_name;
    if (!ready) return;
    coachAutoFiredRef.current = true;
    (async () => {
      setCoachLoading(true);
      try {
        const { data } = await api.post(
          `/live-rooms/${roomId}/coach-questions`
        );
        setCoachQuestions(data.questions || []);
      } catch {
        /* user can still hit Generate manually */
      } finally {
        setCoachLoading(false);
      }
    })();
  }, [room, roomId]);

  // Init local media + signaling once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!room) return;
      try {
        // Reuse existing stream across re-mounts (React StrictMode dev)
        let stream = localStreamRef.current;
        if (!stream || stream.getTracks().every((t) => t.readyState === "ended")) {
          stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: true,
          });
          if (cancelled) {
            stream.getTracks().forEach((t) => t.stop());
            return;
          }
          localStreamRef.current = stream;
        }
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
          localVideoRef.current.muted = true;
          localVideoRef.current.playsInline = true;
          try {
            await localVideoRef.current.play();
          } catch {
            // Autoplay may need a gesture in rare browsers; the stream is live regardless
          }
        }
        if (callRef.current) {
          // Already started — keep existing peer connection
          return;
        }
        const call = new LiveCall({
          roomId,
          role: "host",
          onRemoteStream: (remote) => {
            if (remoteVideoRef.current) {
              remoteVideoRef.current.srcObject = remote;
              remoteVideoRef.current.playsInline = true;
              remoteVideoRef.current.play().catch(() => {});
            }
            remoteStreamRef.current = remote;
            setHasRemote(true);
            // Stream arrived — clear any pending failure timer
            if (connTimerRef.current) {
              clearTimeout(connTimerRef.current);
              connTimerRef.current = null;
            }
            setConnTimedOut(false);
            if (recordEnabled) startRecording(remote, stream);
          },
          onPeerLeft: () => {
            setHasRemote(false);
            if (connTimerRef.current) {
              clearTimeout(connTimerRef.current);
              connTimerRef.current = null;
            }
            toast.message("Candidate left the call");
          },
          onConnectionState: (s) => setConnState(s),
          onPeerJoined: () => {
            // 15s safety net: if we never receive remote tracks (e.g. NAT/firewall blocks
            // both UDP and TCP TURN), surface a clear error instead of staying black forever.
            if (connTimerRef.current) clearTimeout(connTimerRef.current);
            connTimerRef.current = setTimeout(() => {
              if (!remoteStreamRef.current) setConnTimedOut(true);
            }, 15000);
          },
        });
        callRef.current = call;
        await call.start(stream);
      } catch (e) {
        setPermissionError(
          "Camera and microphone permission required to host the call."
        );
      }
    })();
    return () => {
      cancelled = true;
      // NOTE: do not tear down stream/call here — that runs on every dep change.
      // Real teardown happens in the unmount-only effect below.
    };
  }, [room?.id]); // eslint-disable-line

  // True unmount cleanup
  useEffect(() => {
    return () => {
      try {
        if (callRef.current) callRef.current.hangup();
      } catch {}
      try {
        if (localStreamRef.current) {
          localStreamRef.current.getTracks().forEach((t) => t.stop());
          localStreamRef.current = null;
        }
      } catch {}
    };
  }, []);

  const startRecording = (remoteStream, localStream) => {
    if (!remoteStream || recorderRef.current) return;
    try {
      // 1) Mix both audio tracks (candidate + interviewer) so playback has both voices.
      // 2) Composite both video frames onto a <canvas> — remote (candidate) full-frame
      //    with a small picture-in-picture of the interviewer in the lower-right corner.
      // 3) Capture the canvas + mixed audio into a single MediaStream for MediaRecorder.
      let recordStream = remoteStream;

      // --- AUDIO MIX ---
      let mixedAudioTracks = [];
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (AudioCtx) {
          const ctx = new AudioCtx();
          audioCtxRef.current = ctx;
          const dest = ctx.createMediaStreamDestination();
          let mixed = false;
          for (const s of [remoteStream, localStream]) {
            if (!s) continue;
            const audioTracks = s.getAudioTracks();
            if (audioTracks.length === 0) continue;
            const src = ctx.createMediaStreamSource(
              new MediaStream([audioTracks[0]])
            );
            src.connect(dest);
            mixed = true;
          }
          if (mixed) mixedAudioTracks = dest.stream.getAudioTracks();
        }
      } catch {
        // mixer setup failed — keep mixedAudioTracks empty and fall back below
      }

      // --- VIDEO COMPOSITOR (candidate full-frame + interviewer PIP) ---
      let compositedVideoTrack = null;
      try {
        const W = 1280;
        const H = 720;
        const canvas = document.createElement("canvas");
        canvas.width = W;
        canvas.height = H;
        const ctx2d = canvas.getContext("2d");
        // Fill black initially so the recorder has frames even before videos load
        ctx2d.fillStyle = "#000";
        ctx2d.fillRect(0, 0, W, H);

        const makeHiddenVideo = (stream) => {
          if (!stream || stream.getVideoTracks().length === 0) return null;
          const v = document.createElement("video");
          v.srcObject = stream;
          v.muted = true;
          v.playsInline = true;
          v.autoplay = true;
          v.play().catch(() => {});
          return v;
        };
        const remoteVideo = makeHiddenVideo(remoteStream);
        const localVideo = makeHiddenVideo(localStream);

        const draw = () => {
          // Background = candidate (remote) — cover the full frame
          if (remoteVideo && remoteVideo.readyState >= 2) {
            const vw = remoteVideo.videoWidth || W;
            const vh = remoteVideo.videoHeight || H;
            const scale = Math.max(W / vw, H / vh);
            const dw = vw * scale;
            const dh = vh * scale;
            ctx2d.drawImage(
              remoteVideo,
              (W - dw) / 2,
              (H - dh) / 2,
              dw,
              dh
            );
          }
          // Picture-in-picture = interviewer (local) — bottom-right ~22% width
          if (localVideo && localVideo.readyState >= 2) {
            const pipW = Math.round(W * 0.22);
            const lvw = localVideo.videoWidth || pipW;
            const lvh = localVideo.videoHeight || Math.round(pipW * 0.5625);
            const pipH = Math.round((pipW * lvh) / lvw);
            const x = W - pipW - 24;
            const y = H - pipH - 24;
            // Drop shadow + white border for visibility
            ctx2d.save();
            ctx2d.shadowColor = "rgba(0,0,0,0.5)";
            ctx2d.shadowBlur = 12;
            ctx2d.fillStyle = "#fff";
            ctx2d.fillRect(x - 3, y - 3, pipW + 6, pipH + 6);
            ctx2d.restore();
            ctx2d.drawImage(localVideo, x, y, pipW, pipH);
          }
          compositorRef.current.raf = requestAnimationFrame(draw);
        };
        compositorRef.current = {
          canvas,
          ctx: ctx2d,
          remoteVideo,
          localVideo,
          raf: 0,
        };
        compositorRef.current.raf = requestAnimationFrame(draw);
        const canvasStream = canvas.captureStream(30);
        if (canvasStream.getVideoTracks().length > 0) {
          compositedVideoTrack = canvasStream.getVideoTracks()[0];
        }
      } catch {
        // compositor setup failed — fall back to remote-only video below
        compositedVideoTrack = null;
      }

      // --- COMBINE final recording stream ---
      if (compositedVideoTrack || mixedAudioTracks.length) {
        const combined = new MediaStream();
        if (compositedVideoTrack) {
          combined.addTrack(compositedVideoTrack);
        } else {
          const vt = (remoteStream.getVideoTracks() || [])[0];
          if (vt) combined.addTrack(vt);
        }
        if (mixedAudioTracks.length) {
          for (const t of mixedAudioTracks) combined.addTrack(t);
        } else {
          // Fall back: at least keep remote audio so playback isn't silent
          for (const t of remoteStream.getAudioTracks() || []) combined.addTrack(t);
        }
        recordStream = combined;
      }

      let mime = "video/webm;codecs=vp8,opus";
      if (!window.MediaRecorder || !MediaRecorder.isTypeSupported(mime)) {
        mime = "video/webm";
      }
      const rec = new MediaRecorder(recordStream, { mimeType: mime });
      recordedChunksRef.current = [];
      chunkSeqRef.current = 0;
      chunkUploadFailuresRef.current = 0;
      rec.ondataavailable = (e) => {
        if (!e.data || e.data.size <= 0) return;
        recordedChunksRef.current.push(e.data);
        // Streaming upload: ship each chunk to the backend immediately so a
        // browser crash / network drop only loses the LAST chunk, not the
        // whole call. Back off after 3 consecutive upload failures.
        if (chunkUploadFailuresRef.current >= 3) return;
        const seq = chunkSeqRef.current++;
        const fd = new FormData();
        fd.append("seq", String(seq));
        fd.append("file", e.data, `chunk_${seq}.webm`);
        api
          .post(`/live-rooms/${roomId}/recording/chunk`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 30000,
          })
          .then(() => {
            chunkUploadFailuresRef.current = 0;
          })
          .catch(() => {
            chunkUploadFailuresRef.current += 1;
            // Silent — we still have the chunk in recordedChunksRef as a
            // belt-and-braces backup for the final upload on stopRecording.
          });
      };
      rec.start(2000); // 2-second slices = max 2s of recording lost per crash
      recorderRef.current = rec;
      recordingStartRef.current = Date.now();
      setRecording(true);
    } catch (err) {
      // recording not supported — silent fallback
    }
  };

  const stopRecording = () =>
    new Promise((resolve) => {
      const rec = recorderRef.current;
      if (!rec || rec.state === "inactive") {
        resolve(null);
        return;
      }
      rec.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, {
          type: "video/webm",
        });
        const duration = Date.now() - recordingStartRef.current;
        recorderRef.current = null;
        recordedChunksRef.current = [];
        // Tear down the audio mixer
        if (audioCtxRef.current) {
          try {
            audioCtxRef.current.close();
          } catch {
            // Best-effort — AudioContext.close() can throw on already-closed contexts
          }
          audioCtxRef.current = null;
        }
        // Tear down the video compositor
        if (compositorRef.current) {
          try {
            if (compositorRef.current.raf) {
              cancelAnimationFrame(compositorRef.current.raf);
            }
            for (const v of [
              compositorRef.current.remoteVideo,
              compositorRef.current.localVideo,
            ]) {
              if (v) {
                v.pause();
                v.srcObject = null;
              }
            }
          } catch {
            // Best-effort cleanup — recording is already stopped
          }
          compositorRef.current = null;
        }
        setRecording(false);
        resolve({ blob, duration });
      };
      rec.stop();
    });

  const toggleRecord = async () => {
    if (recording) {
      await stopRecording();
      setRecordEnabled(false);
      toast.message("Recording stopped");
    } else if (remoteStreamRef.current) {
      startRecording(remoteStreamRef.current, localStreamRef.current);
      setRecordEnabled(true);
      toast.success("Recording resumed");
    } else {
      setRecordEnabled(true);
      toast.message("Will start when candidate joins");
    }
  };

  const toggleMute = () => {
    const stream = localVideoRef.current?.srcObject;
    if (!stream) return;
    stream.getAudioTracks().forEach((t) => (t.enabled = muted));
    setMuted(!muted);
  };

  const toggleCamera = () => {
    const stream = localVideoRef.current?.srcObject;
    if (!stream) return;
    stream.getVideoTracks().forEach((t) => (t.enabled = cameraOff));
    setCameraOff(!cameraOff);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(candidateLink);
    toast.success("Candidate link copied");
  };

  const endCall = async () => {
    let uploadResult = null;
    const wasRecording = !!recorderRef.current;
    if (wasRecording) {
      setUploadingRecording(true);
      try {
        const stopped = await stopRecording();
        if (stopped && stopped.blob && stopped.blob.size > 0) {
          // Prefer the chunk-finalize path (chunks are already on the server
          // and crash-safe). Fall back to the legacy single-shot uploader if
          // the backend has no chunks (e.g., older deploy or first chunk
          // hadn't landed yet).
          const fdFinalize = new FormData();
          fdFinalize.append("duration_ms", String(stopped.duration));
          try {
            const { data } = await api.post(
              `/live-rooms/${roomId}/recording/finalize`,
              fdFinalize,
              { headers: { "Content-Type": "multipart/form-data" } }
            );
            uploadResult = data;
          } catch (finalizeErr) {
            // Legacy fallback — re-upload the whole blob
            const fd = new FormData();
            fd.append("duration_ms", String(stopped.duration));
            fd.append("file", stopped.blob, `live-${roomId}.webm`);
            const { data } = await api.post(
              `/live-rooms/${roomId}/recording`,
              fd,
              { headers: { "Content-Type": "multipart/form-data" } }
            );
            uploadResult = data;
          }
        }
      } catch (err) {
        toast.error("Recording could not be uploaded");
      } finally {
        setUploadingRecording(false);
      }
    }
    try {
      if (callRef.current) callRef.current.hangup();
      await api.post(`/live-rooms/${roomId}/end`);
    } catch {}
    if (uploadResult?.interview_id) {
      toast.success(`Recording saved · score ${uploadResult.overall_score}/10`);
      navigate(`/jobs/${room?.job_id}/candidates/${uploadResult.interview_id}`);
    } else {
      toast.success("Call ended");
      navigate(`/jobs/${room?.job_id}`);
    }
  };

  if (loading || !room) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-900 text-white">
        Loading room…
      </main>
    );
  }

  const job = room.job || {};
  const questions = job.questions || [];

  return (
    <main
      data-testid="live-host-page"
      className="grain-overlay min-h-screen bg-slate-900 text-white"
    >
      <div className="mx-auto grid min-h-screen max-w-7xl gap-px bg-white/5 px-6 py-6 lg:grid-cols-12 lg:gap-6">
        {/* Video stage */}
        <section className="flex flex-col gap-4 lg:col-span-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-300">
              <Video className="h-3 w-3" /> Live · {job.title}
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-400">
              <span className="inline-flex items-center gap-1">
                <Wifi className="h-3 w-3" /> {connState}
              </span>
              <span
                data-testid="live-host-candidate-status"
                className="inline-flex items-center gap-1"
              >
                <Users className="h-3 w-3" />{" "}
                {hasRemote
                  ? "Candidate connected"
                  : room.candidate_step === "warmup"
                  ? "Candidate recording warm-up…"
                  : room.candidate_step === "live"
                  ? "Candidate joining…"
                  : room.candidate_step === "form"
                  ? "Candidate filling form…"
                  : "Waiting for candidate…"}
              </span>
            </div>
          </div>

          <div className="relative overflow-hidden border border-white/10 bg-black">
            {recording && hasRemote && (
              <div
                data-testid="live-host-recording-indicator"
                className="absolute left-4 top-4 z-10 flex items-center gap-2 border border-red-500 bg-black/60 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-red-400"
              >
                <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
                Recording
              </div>
            )}
            {uploadingRecording && (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/80 text-center text-white">
                <div>
                  <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-300" />
                  <div className="mt-3 font-heading text-lg">
                    Uploading recording…
                  </div>
                  <div className="mt-1 text-xs text-slate-400">
                    Transcribing + scoring against your questions.
                  </div>
                </div>
              </div>
            )}
            {/* Remote video ALWAYS mounted so ref is available when stream arrives.
                Conditional overlay shown until first track arrives.
                Without this, the React ref-attach race caused a black screen. */}
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              data-testid="live-host-remote-video"
              className={`aspect-video w-full bg-black object-cover ${hasRemote ? "" : "hidden"}`}
            />
            {!hasRemote && (connTimedOut || connState === "failed") && (
              <div
                data-testid="live-host-conn-failed"
                className="flex aspect-video w-full flex-col items-center justify-center bg-black p-6 text-center"
              >
                <div className="inline-flex items-center gap-2 rounded-sm border border-rose-300 bg-rose-50 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-rose-700">
                  <Wifi className="h-3 w-3" /> Connection failed
                </div>
                <div className="mt-4 max-w-md font-heading text-xl text-white">
                  We couldn't establish the video link with your candidate.
                </div>
                <div className="mt-2 max-w-md text-sm text-slate-400">
                  This is usually a restrictive network (corporate VPN, strict firewall,
                  or some hotel WiFi). Ask the candidate to switch to a different network
                  (mobile hotspot / home WiFi) and re-open the link — or try a different
                  device.
                </div>
                <button
                  type="button"
                  data-testid="live-host-conn-retry"
                  onClick={() => window.location.reload()}
                  className="mt-5 rounded-sm border border-white/30 bg-transparent px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-white hover:bg-white/10"
                >
                  Retry connection
                </button>
              </div>
            )}
            {!hasRemote && !connTimedOut && connState !== "failed" && (
              <div
                data-testid="live-host-waiting"
                className="flex aspect-video w-full flex-col items-center justify-center bg-black p-6 text-center text-slate-400"
              >
                <Loader2 className="h-8 w-8 animate-spin text-blue-300" />
                <div className="mt-4 font-heading text-lg">
                  Share the link to bring the candidate in
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Input
                    value={candidateLink}
                    readOnly
                    data-testid="live-host-link-input"
                    className="w-80 rounded-sm border-white/20 bg-white/5 font-mono text-xs text-white"
                  />
                  <Button
                    onClick={copyLink}
                    data-testid="live-host-copy-btn"
                    variant="outline"
                    className="rounded-sm border-white/30 bg-transparent text-white hover:bg-white/10"
                  >
                    <Copy className="mr-2 h-4 w-4" />
                    Copy
                  </Button>
                </div>
              </div>
            )}
            {/* Local PIP */}
            <div className="absolute bottom-4 right-4 h-32 w-44 overflow-hidden border border-white/20 bg-black shadow-lg sm:h-40 sm:w-56">
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                data-testid="live-host-local-video"
                className="h-full w-full object-cover"
              />
            </div>
          </div>

          {permissionError && (
            <div className="border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-300">
              {permissionError}
            </div>
          )}

          {/* Controls */}
          <div className="flex flex-wrap items-center justify-between gap-3 border border-white/10 bg-white/5 p-3">
            <div className="flex flex-wrap gap-2">
              <Button
                data-testid="live-host-mute-btn"
                variant="outline"
                onClick={toggleMute}
                className="rounded-sm border-white/30 bg-transparent text-white hover:bg-white/10"
              >
                {muted ? "Unmute" : "Mute"}
              </Button>
              <Button
                data-testid="live-host-camera-btn"
                variant="outline"
                onClick={toggleCamera}
                className="rounded-sm border-white/30 bg-transparent text-white hover:bg-white/10"
              >
                {cameraOff ? "Camera on" : "Camera off"}
              </Button>
              <Button
                data-testid="live-host-record-btn"
                variant="outline"
                onClick={toggleRecord}
                className={`rounded-sm border-white/30 bg-transparent text-white hover:bg-white/10 ${
                  recording ? "border-red-500/60 text-red-300" : ""
                }`}
              >
                {recording ? (
                  <>
                    <CircleOff className="mr-2 h-4 w-4" /> Stop recording
                  </>
                ) : (
                  <>
                    <Circle className="mr-2 h-4 w-4 fill-current text-red-400" />
                    {recordEnabled ? "Recording" : "Start recording"}
                  </>
                )}
              </Button>
            </div>
            <Button
              data-testid="live-host-end-btn"
              onClick={endCall}
              disabled={uploadingRecording}
              className="rounded-sm bg-red-600 hover:bg-red-500"
            >
              {uploadingRecording ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Uploading…
                </>
              ) : (
                <>
                  <PhoneOff className="mr-2 h-4 w-4" />
                  End call
                </>
              )}
            </Button>
          </div>
        </section>

        {/* AI question sidebar */}
        <aside className="lg:col-span-4">
          <div className="sticky top-6 border border-white/10 bg-white/5 p-5">
            <div className="flex items-center justify-between gap-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-300">
              <span className="inline-flex items-center gap-2">
                <ListChecks className="h-3 w-3" /> AI coach
              </span>
              <Button
                type="button"
                size="sm"
                data-testid="live-host-coach-generate-btn"
                disabled={coachLoading}
                onClick={async () => {
                  setCoachLoading(true);
                  try {
                    const { data } = await api.post(
                      `/live-rooms/${roomId}/coach-questions`
                    );
                    setCoachQuestions(data.questions || []);
                    if (!data.cached)
                      toast.success(`Generated ${data.questions.length} coaching questions`);
                  } catch (err) {
                    toast.error(
                      err?.response?.data?.detail || "AI coach failed"
                    );
                  } finally {
                    setCoachLoading(false);
                  }
                }}
                className="rounded-sm border border-blue-300/30 bg-blue-500/10 text-[10px] font-bold uppercase tracking-[0.16em] text-blue-200 hover:bg-blue-500/20"
              >
                {coachLoading ? (
                  <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                ) : (
                  <Sparkles className="mr-1.5 h-3 w-3" />
                )}
                {coachQuestions.length > 0 ? "Regenerate" : "Generate questions"}
              </Button>
            </div>
            <p className="mt-2 text-xs text-slate-400">
              Tap a question to copy + mark it as asked. Only you can see these.
            </p>
            {coachQuestions.length > 0 ? (
              <ol className="mt-4 space-y-3">
                {coachQuestions.map((q, i) => {
                  const asked = askedQuestions[q.id];
                  return (
                    <li
                      key={q.id}
                      data-testid={`live-host-coach-q-${i}`}
                      className={`group flex cursor-pointer gap-3 border-b border-white/5 pb-3 text-sm last:border-0 ${
                        asked ? "opacity-50" : "hover:bg-white/5"
                      }`}
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(q.text);
                        } catch {
                          /* ignore */
                        }
                        setAskedQuestions((prev) => ({ ...prev, [q.id]: Date.now() }));
                        toast.success("Copied to clipboard");
                      }}
                    >
                      <span className="mt-0.5 font-mono text-xs text-blue-300">
                        {asked ? "✓" : String(i + 1).padStart(2, "0")}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className={asked ? "line-through" : ""}>{q.text}</div>
                        {q.why && (
                          <div className="mt-1 text-[11px] italic text-slate-500">
                            Listen for: {q.why}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            ) : questions.length > 0 ? (
              <ol className="mt-4 space-y-3">
                {questions.map((q, i) => (
                  <li
                    key={q.id}
                    data-testid={`live-host-question-${i}`}
                    className="flex gap-3 border-b border-white/5 pb-3 text-sm last:border-0"
                  >
                    <span className="font-mono text-blue-300">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span>{q.text}</span>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="mt-4 text-sm text-slate-400">
                Click <em>Generate questions</em> to get 3 candidate-specific
                prompts from GPT, tailored to {room.candidate_name || "the candidate"}'s
                résumé and the role.
              </p>
            )}
            {room.candidate_name && (
              <div className="mt-6 border-t border-white/10 pt-4">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                  Candidate
                </div>
                <div className="mt-1 font-heading text-base">
                  {room.candidate_name}
                </div>
                <div className="text-xs text-slate-400">
                  {room.candidate_email}
                </div>
              </div>
            )}

            {/* Interviewer notes — auto-saved every 1.5s of idle typing */}
            <div className="mt-6 border-t border-white/10 pt-4">
              <div className="flex items-center justify-between gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-blue-300">
                <span className="inline-flex items-center gap-1.5">
                  <Pencil className="h-3 w-3" />
                  Interviewer notes
                </span>
                <span
                  data-testid="live-host-notes-status"
                  className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500"
                >
                  {notesSaving ? "Saving…" : notesSavedAt ? "Saved" : ""}
                </span>
              </div>
              <textarea
                data-testid="live-host-notes-textarea"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={8}
                maxLength={20000}
                placeholder="Jot down what stood out, follow-up questions, red flags, anything you want in the post-call review…"
                className="mt-2 w-full resize-y rounded-sm border border-white/10 bg-slate-950/40 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-blue-400 focus:outline-none"
              />
              <p className="mt-1 text-[10px] text-slate-500">
                Auto-saves as you type · also visible on the post-call review
                dashboard alongside the AI scoring.
              </p>
              <Button
                type="button"
                size="sm"
                data-testid="live-host-notes-summarize-btn"
                disabled={summarizing || notesSaving || (notes || "").trim().length < 20}
                onClick={async () => {
                  setSummarizing(true);
                  try {
                    const { data } = await api.post(
                      `/live-rooms/${roomId}/summarize-notes`
                    );
                    setNotesSummary(data);
                  } catch (err) {
                    toast.error(
                      err?.response?.data?.detail ||
                        "Could not summarize notes"
                    );
                  } finally {
                    setSummarizing(false);
                  }
                }}
                className="mt-2 w-full rounded-sm border border-violet-400/30 bg-violet-500/10 text-xs font-bold uppercase tracking-[0.16em] text-violet-200 hover:bg-violet-500/20"
              >
                {summarizing ? (
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Sparkles className="mr-2 h-3.5 w-3.5" />
                )}
                {summarizing ? "Summarizing…" : "AI summarize my notes"}
              </Button>
              {notesSummary && (
                <div
                  data-testid="live-host-notes-summary"
                  className="mt-3 space-y-3 rounded-sm border border-violet-400/30 bg-violet-500/5 p-3 text-xs text-slate-200"
                >
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-300">
                      Strengths
                    </div>
                    <ul className="mt-1 list-disc space-y-1 pl-4">
                      {(notesSummary.strengths || []).map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-300">
                      Concerns
                    </div>
                    <ul className="mt-1 list-disc space-y-1 pl-4">
                      {(notesSummary.concerns || []).map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </div>
                  {notesSummary.next_step && (
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-blue-300">
                        Recommended next step
                      </div>
                      <p className="mt-1 leading-snug">{notesSummary.next_step}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
}
